// @mui material components
import { Theme } from '@mui/material/styles';

interface NavbarState {
    transparentNavbar?: boolean;
    absolute?: boolean;
    light?: boolean;
    darkMode?: boolean;
    isMini?: boolean;
}

function navbar(theme: Theme, ownerState: NavbarState) {
    const { palette, boxShadows, functions, transitions, breakpoints, borders } = theme;
    const { transparentNavbar, absolute, light, darkMode } = ownerState;

    const { dark, white, transparent, background } = palette;
    const { navbarBoxShadow } = boxShadows;
    const { pxToRem } = functions;
    const { borderRadius } = borders;

    return {
        boxShadow: transparentNavbar || absolute ? 'none' : navbarBoxShadow,
        backdropFilter:
            transparentNavbar || absolute ? 'none' : `saturate(200%) blur(${pxToRem(30)})`,
        // No escuro a barra tem a cor do menu lateral mesmo parada no topo. O
        // cinza que ela tinha ali não era cor: era o clareamento automático do
        // Paper do MUI, que o tema desligou.
        backgroundColor: absolute
            ? `${transparent.main} !important`
            : darkMode
              ? `${background.sidenav} !important`
              : transparentNavbar
                ? `${transparent.main} !important`
                // Opaca: translúcida, o conteúdo rolando por baixo aparecia
                // através da barra e embaralhava o título.
                : white.main,

        color: () => {
            let color = light || darkMode ? white.main : dark.main;

            if (transparentNavbar && !light) {
                color = darkMode ? white.main : dark.main;
            }

            return color;
        },
        top: absolute ? 0 : pxToRem(12),
        minHeight: pxToRem(75),
        display: 'grid',
        alignItems: 'center',
        borderRadius: borderRadius.xl,
        paddingTop: pxToRem(8),
        paddingBottom: pxToRem(8),
        paddingRight: absolute ? pxToRem(8) : 0,
        paddingLeft: absolute ? pxToRem(16) : 0,

        '& > *': {
            transition: transitions.create('all', {
                easing: transitions.easing.easeInOut,
                duration: transitions.duration.standard,
            }),
        },

        '& .MuiToolbar-root': {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',

            [breakpoints.up('sm')]: {
                minHeight: 'auto',
                padding: `${pxToRem(4)} ${pxToRem(16)}`,
            },
        },
    };
}

const navbarContainer = ({ breakpoints }: Theme, { darkMode }: NavbarState) => ({
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    pt: 0.5,
    pb: 0.5,

    [breakpoints.up('md')]: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingTop: '0',
        paddingBottom: '0',
    },
    color: ({ palette: { dark, white } }: Theme) => (darkMode ? white.main : dark.main),
});

const navbarRow = ({ breakpoints }: Theme, { isMini, darkMode }: NavbarState) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',

    [breakpoints.up('md')]: {
        justifyContent: isMini ? 'space-between' : 'stretch',
        width: isMini ? '100%' : 'max-content',
    },

    [breakpoints.up('xl')]: {
        justifyContent: 'stretch !important',
        width: 'max-content !important',
    },
    color: ({ palette: { dark, white } }: Theme) => (darkMode ? white.main : dark.main),
});

const navbarIconButton = ({ typography: { size }, breakpoints }: Theme, { darkMode }: NavbarState) => ({
    px: 1,

    '& .material-icons, .material-icons-round': {
        fontSize: `${size['2xl']} !important`,
        color: ({ palette: { dark, white } }: Theme) => (darkMode ? white.main : dark.main),
    },

    '& .MuiTypography-root': {
        display: 'none',

        [breakpoints.up('sm')]: {
            display: 'inline-block',
            lineHeight: 1.2,
            ml: 0.5,
            color: ({ palette: { dark, white } }: Theme) => (darkMode ? white.main : dark.main),
        },
    },
});

const navbarDesktopMenu = ({ breakpoints }: Theme, { darkMode }: NavbarState) => ({
    display: 'none !important',
    cursor: 'pointer',
    bottom: '-2px',

    [breakpoints.up('xl')]: {
        display: 'inline-block !important',
    },
    color: ({ palette: { dark, white } }: Theme) => (darkMode ? white.main : dark.main),
});

const navbarMobileMenu = ({ breakpoints }: Theme, { darkMode }: NavbarState) => ({
    display: 'inline-block',
    lineHeight: 0,

    [breakpoints.up('xl')]: {
        display: 'none',
    },
    color: ({ palette: { dark, white } }: Theme) => (darkMode ? white.main : dark.main),
});


export {
    navbar,
    navbarContainer,
    navbarRow,
    navbarIconButton,
    navbarDesktopMenu,
    navbarMobileMenu,
};
